import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import * as Sentry from "@sentry/browser";
import { Button, Modal, Spin, Table, Tag, Tooltip, message } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import { AxiosResponse } from "axios";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import { GET_HOLIDAYS, UPDATE_HOLIDAY } from "../gql/holidays";
import {
  APIError,
  FormFieldConfig,
  Holiday,
} from "../helpers/shared-interfaces";
import { handleValidationErrors, request } from "../helpers/utilities";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isLoading: boolean;
  isSaving: boolean;
  holidays: Holiday[];
  showModal: boolean;
  selectedHoliday?: Holiday;
  isRecurring: boolean;
}

const holidayColumns = (
  onEdit: (holiday: Holiday) => void,
): Array<Record<string, unknown>> => [
  {
    title: "Holiday",
    dataIndex: "title",
    key: "title",
    render(text: string): JSX.Element {
      return <div>{text}</div>;
    },
  },
  {
    title: "Date",
    key: "eventDate",
    render(_: unknown, record: Holiday): JSX.Element {
      const date = new Date(record.eventDate);
      const formatted = record.recurring
        ? date.toLocaleDateString("en-GB", { day: "numeric", month: "long" })
        : date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
      return <div>{formatted}</div>;
    },
  },
  {
    title: "Status",
    dataIndex: "isActive",
    key: "isActive",
    render(isActive: boolean): JSX.Element {
      return isActive ? (
        <Tag color="green">Active</Tag>
      ) : (
        <Tag color="default">Inactive</Tag>
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    align: "center" as const,
    render(_: unknown, record: Holiday): JSX.Element | null {
      if (record.recurring) return null;
      return (
        <Tooltip title="Edit">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          >
            Edit
          </Button>
        </Tooltip>
      );
    },
  },
];

export default class Holidays extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.reference = React.createRef<FormInstance>();
    this.state = {
      isLoading: false,
      isSaving: false,
      holidays: [],
      showModal: false,
      isRecurring: true,
    };
  }

  componentDidMount(): void {
    this.setState({ isLoading: true }, this.getHolidays);
  }

  getHolidays = async (): Promise<void> => {
    try {
      const {
        data: { data },
      }: AxiosResponse<{ data: { holidays: Holiday[] } }> = await request({
        query: GET_HOLIDAYS,
        variables: { includeInactive: true },
      });

      this.setState({
        holidays: data.holidays,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
      message.error(`Failed to load holidays - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  openModal = (holiday?: Holiday): void => {
    this.setState({
      selectedHoliday: holiday,
      showModal: true,
      isRecurring: holiday ? holiday.recurring : true,
    });
  };

  closeModal = (): void => {
    this.reference.current?.resetFields();
    this.setState({
      showModal: false,
      selectedHoliday: undefined,
      isRecurring: true,
    });
  };

  saveHoliday = async (): Promise<void> => {
    interface Response {
      data?: {
        createHoliday?: { holiday?: Holiday; errors?: APIError[] };
        updateHoliday?: { holiday?: Holiday; errors?: APIError[] };
      };
    }

    if (!this.reference.current) return;

    let data;
    try {
      data = await this.reference.current.validateFields(["eventDate"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const { selectedHoliday, isRecurring } = this.state;

    const eventDate = data.eventDate
      ? isRecurring
        ? data.eventDate.format("2000-MM-DD")
        : data.eventDate.format("YYYY-MM-DD")
      : undefined;

    this.setState({ isSaving: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_HOLIDAY,
        variables: { id: selectedHoliday?.id, title: data.title, eventDate },
      });

      const payload = response.data.data?.updateHoliday;

      if (payload?.errors && payload.errors.length > 0) {
        message.error(payload.errors[0].description);
      } else {
        message.success("Holiday updated");
        this.closeModal();
        this.setState({ isLoading: true }, this.getHolidays);
      }
    } catch (error) {
      message.error(`Failed to save holiday - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSaving: false });
    }
  };

  render(): JSX.Element {
    const {
      isLoading,
      isSaving,
      holidays,
      showModal,
      selectedHoliday,
      isRecurring,
    } = this.state;

    const fields: FormFieldConfig[] = [
      {
        name: "eventDate",
        label: "Date",
        inputType: "defaultDate",
        rule: "object",
        initialValue: selectedHoliday?.eventDate
          ? moment(selectedHoliday.eventDate)
          : undefined,
        required: true,
        colConfig: { span: 24 },
        elementStyle: { width: "100%" },
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Modal
            title="Edit Holiday"
            visible={showModal}
            onCancel={this.closeModal}
            onOk={this.saveHoliday}
            okText="Save"
            okButtonProps={{ className: "base-button", loading: isSaving }}
            destroyOnClose
          >
            <Spin spinning={isSaving}>
              <BaseForm
                key={selectedHoliday?.id}
                layout="vertical"
                reference={this.reference}
                fieldsToHide={[]}
                fields={fields}
              />
            </Spin>
          </Modal>

          <Table
            bordered
            columns={holidayColumns(this.openModal)}
            dataSource={[...holidays]
              .sort((a, b) => {
                const da = new Date(a.eventDate);
                const db = new Date(b.eventDate);
                const monthDiff = da.getMonth() - db.getMonth();
                return monthDiff !== 0
                  ? monthDiff
                  : da.getDate() - db.getDate();
              })
              .map((h) => ({ ...h, key: h.id }))}
            pagination={false}
          />
        </Card>
      </Spin>
    );
  }
}
